import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClient, useMutation, useQuery } from "urql";
import { useQueries } from "@tanstack/react-query";
import { isEqual } from "lodash-es";
import { useDeepMemo, usePolling, useRetryWithBackoff } from "../utils/index.js";
function graphql_useQuery(args) {
    const [state, refetch] = useQuery(args);
    const refetchFromNetwork = useCallback((opts)=>{
        refetch({
            requestPolicy: 'network-only',
            ...opts
        });
    }, [
        refetch
    ]);
    const requestInProgress = state.fetching || state.stale;
    let pollInterval = args.pollInterval;
    if (true === args.pause || requestInProgress || void 0 !== state.error) pollInterval = void 0;
    usePolling(refetchFromNetwork, pollInterval);
    let errorToRetry = state.error;
    if (true === args.pause || true !== args.autoRetryErrors) errorToRetry = void 0;
    const secondsUntilRetry = useRetryWithBackoff(refetchFromNetwork, requestInProgress, errorToRetry);
    const isLoading = state.fetching && (void 0 === state.data || null === state.data);
    const isFetching = state.fetching;
    const response = useMemo(()=>[
            {
                ...state,
                data: state.data,
                secondsUntilRetry,
                isLoading,
                isFetching
            },
            refetchFromNetwork
        ], [
        state,
        refetchFromNetwork,
        secondsUntilRetry,
        isLoading,
        isFetching
    ]);
    return response;
}
function graphql_useQueries({ queries, context }) {
    const client = useClient();
    const mappedQueries = queries.map(({ onResponseSuccess = (response)=>response, queryArgs, ...rest })=>({
            ...rest,
            queryFn: async ()=>{
                const response = await client.query(queryArgs.query, queryArgs.variables, queryArgs.context).toPromise();
                return onResponseSuccess(response);
            }
        }));
    return useQueries({
        queries: mappedQueries,
        context: context
    });
}
function graphql_useMutation(query) {
    const [state, callback] = useMutation(query);
    const wrapped = async (...execArgs)=>{
        const result = await callback(...execArgs);
        if (result.error) throw result.error;
        return result;
    };
    return [
        state,
        wrapped
    ];
}
const INITIAL_QUERY_STATE = {
    fetching: false
};
function useLazyQuery(args) {
    const client = useClient();
    const [queryState, setQueryState] = useState(INITIAL_QUERY_STATE);
    const inFlightAbort = useRef();
    args = useDeepMemo(()=>args, [
        args
    ]);
    useEffect(()=>()=>{
            if (void 0 !== inFlightAbort.current) {
                inFlightAbort.current.abort();
                inFlightAbort.current = void 0;
            }
            setQueryState(INITIAL_QUERY_STATE);
        }, [
        args
    ]);
    const executeQuery = useCallback(async (execArgs)=>{
        if (void 0 !== inFlightAbort.current) inFlightAbort.current.abort();
        const abortController = new AbortController();
        inFlightAbort.current = abortController;
        setQueryState({
            fetching: true,
            error: void 0,
            data: void 0
        });
        const ctx = {
            ...execArgs.context
        };
        ctx.fetchOptions ??= {};
        if ('function' == typeof ctx.fetchOptions) ctx.fetchOptions = ctx.fetchOptions();
        ctx.fetchOptions.signal = abortController.signal;
        const result = await client.query(args.query, execArgs.variables, execArgs.context).toPromise();
        if (false === abortController.signal.aborted) setQueryState({
            fetching: false,
            error: result.error,
            data: result.data
        });
    }, [
        client,
        args
    ]);
    return [
        queryState,
        executeQuery
    ];
}
const defaultGetPaginationVariables = ({ after, pageSize })=>({
        after,
        pageSize
    });
function usePaginatedQuery({ query, defaultPageSize = 100, additionalVariables, context, getPaginationData, getPaginationVariables = defaultGetPaginationVariables, append = false, pause = false }) {
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [page, setPage] = useState(0);
    const accumulatedRowsRef = useRef([]);
    const processedPagesRef = useRef(new Set());
    const resetCursor = useCallback(()=>{
        setAllCursors([
            null
        ]);
        setPage(0);
        accumulatedRowsRef.current = [];
        processedPagesRef.current = new Set();
    }, []);
    const updatePageSize = useCallback((newPageSize)=>{
        setPageSize(newPageSize);
        resetCursor();
    }, [
        resetCursor
    ]);
    const addCursorToMemory = (cursor)=>{
        if (!allCursors.includes(cursor)) setAllCursors(allCursors.concat(cursor));
    };
    const [allCursors, setAllCursors] = useState([
        null
    ]);
    const currentCursor = allCursors[page];
    const paginationVariables = getPaginationVariables({
        pageSize,
        after: currentCursor
    });
    const [{ data, fetching, error, stale }] = graphql_useQuery({
        query,
        variables: {
            ...paginationVariables,
            ...additionalVariables
        },
        pause,
        context
    });
    const { rows = [], rowCount = 0, cursor = null, hasNextPage } = data ? getPaginationData(data) : {};
    const displayRows = useMemo(()=>{
        if (!append) {
            accumulatedRowsRef.current = [];
            processedPagesRef.current = new Set();
            return rows;
        }
        if (!fetching && rows.length > 0 && !processedPagesRef.current.has(page)) {
            accumulatedRowsRef.current = [
                ...accumulatedRowsRef.current,
                ...rows
            ];
            processedPagesRef.current.add(page);
        }
        return accumulatedRowsRef.current;
    }, [
        append,
        rows,
        fetching,
        page
    ]);
    const setPageState = (newState)=>{
        if (newState.pageSize !== pageSize) return void updatePageSize(newState.pageSize);
        if (newState.page !== page) {
            if (void 0 === hasNextPage) setPage(newState.page);
            else if (hasNextPage || newState.page <= page) setPage(newState.page);
        }
    };
    useEffect(()=>{
        if (null != cursor) addCursorToMemory(cursor);
    }, [
        cursor
    ]);
    return {
        pageSize,
        setPageSize: updatePageSize,
        page,
        setPage,
        setPageState,
        fetching,
        error,
        rows: displayRows,
        rowCount,
        cursor: currentCursor,
        endCursor: cursor,
        hasNextPage: void 0 === hasNextPage ? false : hasNextPage,
        stale,
        resetCursor
    };
}
const DEFAULT_PAGE_SIZE = 100;
function usePaginatedQueryGetFullList({ query, additionalVariables, defaultPageSize = DEFAULT_PAGE_SIZE, context, getPaginationData, maxFetchCount = 1 / 0, pause = false }) {
    const [variables, setVariables] = useState({
        after: '',
        pageSize: defaultPageSize,
        filters: null
    });
    const { rows, rowCount, endCursor, hasNextPage, fetching: loading, error, stale } = usePaginatedQuery({
        query,
        getPaginationData,
        pause: 0 === variables.pageSize || pause,
        additionalVariables: {
            ...variables,
            ...additionalVariables
        },
        context
    });
    const results = useRef([]);
    const mergedResults = useRef([]);
    const fetchCount = useRef(0);
    const isLoading = useRef(false);
    const isStale = useRef(false);
    const completedFetch = !loading && isLoading.current;
    const completedRefetch = !stale && isStale.current;
    if ((completedFetch || completedRefetch) && !isEqual(rows, mergedResults.current)) {
        fetchCount.current = fetchCount.current + 1;
        if ('' === variables.after) results.current = rows;
        else results.current = results.current.concat(rows);
        mergedResults.current = rows;
    }
    if (isLoading.current !== loading) isLoading.current = loading;
    if (isStale.current !== stale) isStale.current = stale;
    useEffect(()=>{
        if (!loading && !stale && hasNextPage && endCursor && endCursor !== variables.after && fetchCount.current < maxFetchCount) setVariables({
            ...variables,
            after: endCursor
        });
    }, [
        endCursor,
        hasNextPage,
        loading,
        maxFetchCount,
        stale,
        variables
    ]);
    return {
        results: results.current,
        loading: fetchCount.current < maxFetchCount && (hasNextPage || loading || stale),
        totalCount: rowCount ?? 0,
        error
    };
}
const graphql_executeQuery = async (client, query, variables, context)=>{
    const result = await client.query(query, variables, context).toPromise();
    if (result.error) throw result.error;
    if (!result.data) throw new Error('No data');
    return result.data;
};
const executeMutation = async (client, mutation, variables, context)=>{
    const result = await client.mutation(mutation, variables, context).toPromise();
    if (result.error) throw result.error;
    if (!result.data) throw new Error('No data');
    return result.data;
};
export { DEFAULT_PAGE_SIZE, executeMutation, graphql_executeQuery as executeQuery, useLazyQuery, graphql_useMutation as useMutation, usePaginatedQuery, usePaginatedQueryGetFullList, graphql_useQueries as useQueries, graphql_useQuery as useQuery };

//# sourceMappingURL=graphql.js.map